// src/pages/Dashboard/NuevoUsuario.jsx

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Alert,
  Snackbar,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import usuarioService from "../../services/usuarioService";
import "../../styles/dashboard.css";

const NuevoUsuario = ({ onUsuarioCreado, onCancelar }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Estados del formulario
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    password2: "",
    first_name: "",
    last_name: "",
    rol_principal: "SOCIO",
    is_staff: false,
    is_active: true,
    socio_id: null,
  });

  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // Estados para búsqueda de socios
  const [socios, setSocios] = useState([]);
  const [socioSelected, setSocioSelected] = useState(null);
  const [loadingSocios, setLoadingSocios] = useState(false);
  const socioSearchTimeout = useRef(null);
  const ROLE_NAME_MAP = {
    ADMIN: ["ADMIN", "ADMINISTRADOR"],
    ASISTENTE: ["ASISTENTE"],
    REVISOR: ["REVISOR"],
    SOCIO: ["SOCIO"],
  };

  const normalizeRoleName = (value) => (value || "").toString().trim().toUpperCase();

  const resolveUsuarioId = async (usuarioCreado) => {
    const directId = usuarioCreado?.id || usuarioCreado?.user?.id;
    if (directId) return directId;

    const lookup = await usuarioService.obtenerUsuarios({ nombre: formData.username });
    const usuarios = lookup?.results || lookup || [];
    const exact = usuarios.find((item) => item.username === formData.username);
    return exact?.id || usuarios[0]?.id || null;
  };

  const asignarRolNegocio = async (usuarioId) => {
    const rolesData = await usuarioService.obtenerRoles();
    const roles = rolesData?.results || rolesData || [];
    const expectedNames = ROLE_NAME_MAP[formData.rol_principal] || [formData.rol_principal];
    const expectedSet = new Set(expectedNames.map(normalizeRoleName));
    const roleMatch = roles.find((role) =>
      expectedSet.has(normalizeRoleName(role?.nombre))
    );

    if (!roleMatch?.id) {
      throw new Error(`No se encontró el rol "${formData.rol_principal}" en catálogo`);
    }

    await usuarioService.asignarRolAuthUsuario(usuarioId, roleMatch.id);
  };

  /**
   * 🔐 Verificar permisos
   */
  useEffect(() => {
    const isAdmin =
      user?.is_superuser || user?.rol_principal === "ADMIN" || user?.is_staff;

    if (!isAdmin) {
      setError("No tienes permisos para crear usuarios");
      setOpenSnackbar(true);
      navigate("/dashboard");
    }
  }, [user, navigate]);

  /**
   * 🔍 Buscar socios
   */
  const buscarSocios = async (query = "") => {
    try {
      setLoadingSocios(true);
      const pageSize = 100;

      if (!query) {
        const data = await usuarioService.buscarSocios("", { pageSize });
        const sociosArray = data.results || data;
        setSocios(sociosArray);
        return;
      }

      let page = 1;
      let acumulados = [];
      // Traer todas las páginas del resultado para no limitar a los primeros 5
      while (true) {
        const data = await usuarioService.buscarSocios(query, { page, pageSize });
        const sociosArray = data.results || data;
        acumulados = acumulados.concat(sociosArray);
        if (!data.next || sociosArray.length < pageSize) {
          break;
        }
        page += 1;
      }
      setSocios(acumulados);
    } catch (err) {
      console.error("Error al buscar socios:", err);
    } finally {
      setLoadingSocios(false);
    }
  };

  const scheduleBuscarSocios = (query) => {
    if (socioSearchTimeout.current) {
      clearTimeout(socioSearchTimeout.current);
    }
    socioSearchTimeout.current = setTimeout(() => {
      buscarSocios(query);
    }, 300);
  };

  useEffect(() => {
    buscarSocios();
    return () => {
      if (socioSearchTimeout.current) {
        clearTimeout(socioSearchTimeout.current);
      }
    };
  }, []);

  /**
   * 🔄 Manejo de cambios en el formulario
   */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /**
   * 🔄 Manejo de selección de socio
   */
  const handleSocioChange = (event, newValue) => {
    setSocioSelected(newValue);
    setFormData((prev) => ({
      ...prev,
      socio_id: newValue?.id || null,
      email: newValue?.email || prev.email,
      first_name: newValue?.nombres || prev.first_name,
      last_name: newValue?.apellidos || prev.last_name,
    }));
  };

  /**
   * ✅ Validar formulario
   */
  const validarFormulario = () => {
    if (!formData.username) {
      setError("El nombre de usuario es obligatorio");
      return false;
    }

    if (!formData.email) {
      setError("El correo electrónico es obligatorio");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("El correo electrónico no es válido");
      return false;
    }

    if (!formData.password) {
      setError("La contraseña es obligatoria");
      return false;
    }

    if (formData.password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres");
      return false;
    }

    if (formData.password !== formData.password2) {
      setError("Las contraseñas no coinciden");
      return false;
    }

    return true;
  };

  /**
   * 📤 Enviar formulario
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!validarFormulario()) {
      setOpenSnackbar(true);
      return;
    }

    try {
      setLoading(true);

      // Preparar datos para enviar
      const dataToSend = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        password2: formData.password2,
        first_name: formData.first_name,
        last_name: formData.last_name,
        rol_principal: formData.rol_principal,
        is_staff: formData.is_staff,
        is_active: formData.is_active,
      };

      // Solo enviar socio_id si se seleccionó uno
      if (formData.socio_id) {
        dataToSend.socio_id = formData.socio_id;
      }

      const nuevoUsuario = await usuarioService.crearUsuario(dataToSend);
      const nuevoUsuarioId = await resolveUsuarioId(nuevoUsuario);

      if (!nuevoUsuarioId) {
        throw new Error("No se pudo resolver el ID del usuario creado");
      }

      await asignarRolNegocio(nuevoUsuarioId);

      if (formData.is_staff) {
        const nuevoUsuarioEsStaff = nuevoUsuario?.is_staff ?? nuevoUsuario?.user?.is_staff;
        if (!nuevoUsuarioEsStaff) {
          await usuarioService.toggleStaff(nuevoUsuarioId);
        }
      }

      setSuccess(true);
      setOpenSnackbar(true);

      // Volver al listado después de crear
      setTimeout(() => {
        if (onUsuarioCreado) {
          onUsuarioCreado();
          return;
        }
        navigate("/dashboard/usuarios");
      }, 1200);
    } catch (err) {
      console.error("Error al crear usuario:", err);
      setError(err.message || "Error al crear usuario");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 📊 Renderizado
   */
  return (
    <div className="main-content">
      <h2 className="dashboard-title">Crear Nuevo Usuario</h2>

      <Box component="form" onSubmit={handleSubmit} className="nuevo-usuario-form">
        {/* Información importante */}
        <Alert severity="info" className="nuevo-usuario-alert-info">
          <strong>Importante:</strong> Solo los usuarios marcados como "Staff" tendrán
          acceso al dashboard. Los socios sin staff solo pueden completar su perfil.
        </Alert>

        {/* Selección de Socio (opcional) */}
        <Box className="nuevo-usuario-socios">
          <Autocomplete
            options={socios}
            getOptionLabel={(option) =>
              `${option.apellidos} ${option.nombres} - ${option.email}`
            }
            value={socioSelected}
            onChange={handleSocioChange}
            onInputChange={(event, value, reason) => {
              if (reason === "input") {
                scheduleBuscarSocios(value);
              } else if (reason === "clear") {
                scheduleBuscarSocios("");
              }
            }}
            loading={loadingSocios}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Vincular con Socio (opcional)"
                helperText="Busca un socio existente para vincular con este usuario"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingSocios ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>

        {/* Datos del Usuario */}
        <Box className="nuevo-usuario-grid">
          <TextField
            label="Nombre de Usuario *"
            name="username"
            value={formData.username}
            onChange={handleChange}
            required
            fullWidth
          />

          <TextField
            label="Correo Electrónico *"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            fullWidth
          />

          <TextField
            label="Nombre"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            fullWidth
          />

          <TextField
            label="Apellidos"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            fullWidth
          />

          <TextField
            label="Contraseña *"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            required
            helperText="Mínimo 8 caracteres"
            fullWidth
          />

          <TextField
            label="Confirmar Contraseña *"
            name="password2"
            type="password"
            value={formData.password2}
            onChange={handleChange}
            required
            fullWidth
          />
        </Box>

        {/* Rol y Permisos */}
        <Box className="nuevo-usuario-permisos">
          <FormControl fullWidth className="nuevo-usuario-rol-control">
            <InputLabel>Rol Principal *</InputLabel>
            <Select
              name="rol_principal"
              value={formData.rol_principal}
              onChange={handleChange}
              label="Rol Principal *"
              className="nuevo-usuario-rol-select"
              MenuProps={{
                PaperProps: {
                  className: "nuevo-usuario-rol-menu",
                },
              }}
            >
              <MenuItem value="SOCIO">Socio</MenuItem>
              <MenuItem value="ASISTENTE">Asistente</MenuItem>
              <MenuItem value="REVISOR">Revisor</MenuItem>
              <MenuItem value="ADMIN">Administrador</MenuItem>
            </Select>
          </FormControl>

          <Box className="nuevo-usuario-checkbox-row">
            <FormControlLabel
              control={
                <Checkbox
                  name="is_staff"
                  checked={formData.is_staff}
                  onChange={handleChange}
                />
              }
              label="Miembro del Staff (acceso al dashboard)"
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
              }
              label="Usuario Activo"
            />
          </Box>
        </Box>

        {/* Descripción de roles */}
        <Alert severity="warning" className="nuevo-usuario-alert-warning">
          <strong>Roles:</strong>
          <ul className="nuevo-usuario-roles-list">
            <li><strong>ADMINISTRADOR:</strong> Control total del sistema</li>
            <li><strong>ASISTENTE:</strong> Crear/editar cursos, noticias y eventos y gestion de credenciales</li>
            <li><strong>REVISOR:</strong> Gestionar proyectos</li>
            <li><strong>SOCIO:</strong> Ver proyectos asignados, noticias y cursos</li>
          </ul>
        </Alert>

        {/* Botones */}
        <Box className="nuevo-usuario-actions">
          <Button
            variant="outlined"
            onClick={() => {
              if (onCancelar) {
                onCancelar();
                return;
              }
              navigate("/dashboard/usuarios");
            }}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <CircularProgress size={20} className="nuevo-usuario-spinner" color="inherit" />
                Creando...
              </>
            ) : (
              "Crear Usuario"
            )}
          </Button>
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={success ? "success" : "error"}
          className="nuevo-usuario-snackbar-alert"
        >
          {success
            ? "Usuario creado exitosamente. Redirigiendo..."
            : error || "Error al crear usuario"}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default NuevoUsuario;
